import React, { useEffect, useState } from "react";
import Input from "../../../form/Input";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import useMutation from "../../../../hooks/useMutation";
import { paymentMethodAdminAddEdit } from "../../../../services/PaymentService";
import FileUploadSingle from "../../../form/FileUploadSingle";
import Checkbox from "../../../form/Checkbox";
import Radio from "../../../form/Radio";
import Textarea from "../../../form/Textarea";
import Editor from "../../../form/Editor";

let validation = {
  name: { required: true, message: "Method name is required" },
  image: { required: true, message: "image is required" },
};

const ManagePayment = ({
  getResponse = () => {},
  close = () => {},
  details,
}) => {
  const [selectedOption, setSelectedOption] = useState("bank");
  const [uploadSection, setUploadSection] = useState("");
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    initialState: {
      appPointField: [{ point: "" }],
      bankPointField: [{ point: "" }],
    },
    validationRules: validation,
  });

  validation.paymentImage = {
    required:
      selectedOption === "app" && uploadSection === "scan" ? true : false,
    message: "Scanner image is required",
  };

  validation.uploadSection = {
    required: selectedOption === "app" && !uploadSection ? true : false,
    message: "Please select either scanner or link option",
  };

  validation.paymentUrl = {
    required:
      selectedOption === "app" && uploadSection === "link" ? true : false,
    message: "Payment link is required",
  };

  // Bank-specific validations
  validation.accName = {
    required: selectedOption === "bank" ? true : false,
    message: "Account name is required",
  };

  validation.bankName = {
    required: selectedOption === "bank" ? true : false,
    message: "Bank name is required",
  };

  validation.accNumber = {
    required: selectedOption === "bank" ? true : false,
    message: "Account number is required",
  };

  validation.routingNumber = {
    required: selectedOption === "bank" ? true : false,
    message: "Routing number is required",
  };

  validation.swiftCode = {
    required: selectedOption === "bank" ? true : false,
    message: "SWIFT code is required",
  };

  validation.bankAddress = {
    required: selectedOption === "bank" ? true : false,
    message: "Bank address is required",
  };

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        name: details?.name,
        image: details?.image?.url,
        headingText: details?.appOb?.sectionOne?.headingText,
        appUserName: details?.appOb?.sectionOne?.appUserName,
        appEmail: details?.appOb?.sectionOne?.appEmail,
        appId: details?.appOb?.sectionOne?.appId,
        displayLink: details?.appOb?.sectionOne?.displayLink,
        noteAppPurpose: details?.appOb?.sectionOne?.note,
        paymentImage: details?.paymentImage?.url,
        paymentUrl: details?.paymentUrl,
        city: details?.appOb?.sectionTwo?.city,
        state: details?.appOb?.sectionTwo?.state,
        country: details?.appOb?.sectionTwo?.country,
        appNote: details?.appOb?.sectionTwo?.afterAction?.note,
        appPointField:
          details?.appOb?.sectionTwo?.afterAction?.pointField?.map((item) => ({
            point: item,
          })) || [],
        accName: details?.bankOb?.accName,
        bankName: details?.bankOb?.bankName,
        accNumber: details?.bankOb?.accNumber,
        routingNumber: details?.bankOb?.routingNumber,
        swiftCode: details?.bankOb?.swiftCode,
        bankAddress: details?.bankOb?.bankAddress,
        noteBankPurpose: details?.bankOb?.note,
        bankNote: details?.bankOb?.afterAction?.note,
        bankPointField:
          details?.bankOb?.afterAction?.pointField?.map((item) => ({
            point: item,
          })) || [],
        note: details?.note,
      });
      setSelectedOption(details.isType);
      setUploadSection(details?.isScanOrLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      delete val?.slug;
      delete val?.applicationPayment;
      delete val?.status;
      return await paymentMethodAdminAddEdit({
        name: val?.name,
        image: val?.image,
        isType: selectedOption,
        "appOb[sectionOne][headingText]": val?.headingText,
        "appOb[sectionOne][appUserName]": val?.appUserName,
        "appOb[sectionOne][appEmail]": val?.appEmail,
        "appOb[sectionOne][appId]": val?.appId,
        "appOb[sectionOne][displayLink]": val?.displayLink,
        "appOb[sectionOne][note]": val?.noteAppPurpose,
        ...(uploadSection && { isScanOrLink: uploadSection }),
        ...(uploadSection === "link"
          ? { paymentUrl: values?.paymentUrl }
          : uploadSection === "scan"
          ? { paymentImage: val?.paymentImage }
          : {}),

        "appOb[sectionTwo][city]": val?.city,
        "appOb[sectionTwo][state]": val?.state,
        "appOb[sectionTwo][country]": val?.country,
        "appOb[sectionTwo][afterAction][note]": val?.appNote,
        ...val?.appPointField?.reduce((acc, item, index) => {
          acc[`appOb[sectionTwo][afterAction][pointField][${index}]`] =
            item?.point;
          return acc;
        }, {}),
        "bankOb[accName]": val?.accName,
        "bankOb[bName]": val?.bankName,
        "bankOb[accNumber]": val?.accNumber,
        "bankOb[routingNumber]": val?.routingNumber,
        "bankOb[swiftCode]": val?.swiftCode,
        "bankOb[bankAddress]": val?.bankAddress,
        "bankOb[note]": val?.noteBankPurpose,
        "bankOb[afterAction][note]": val?.bankNote,
        ...val?.bankPointField?.reduce((acc, item, index) => {
          acc[`bankOb[afterAction][pointField][${index}]`] = item?.point;
          return acc;
        }, {}),
        note: val?.note,
        ...(details?._id && { id: details?._id }),
      });
    },
    onSuccess: (res) => {
      getResponse(res);
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    { item: "Bank", value: "bank" },
    { item: "App", value: "app" },
  ];

  const handleRadioChange = (e) => {
    setSelectedOption(e?.target?.value);
  };

  function appPointAdd() {
    setFieldsValue({
      appPointField: [...values?.appPointField, { point: "" }],
    });
  }

  function bankPointAdd() {
    setFieldsValue({
      bankPointField: [...values?.bankPointField, { point: "" }],
    });
  }

  const numberToOrdinal = (n) => {
    const ordinals = [
      "First",
      "Second",
      "Third",
      "Fourth",
      "Fifth",
      "Sixth",
      "Seventh",
      "Eighth",
      "Ninth",
      "Tenth",
    ];
    return ordinals[n] || `${n + 1}th`;
  };

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="grid grid-cols-12 gap-x-4 gap-y-4">
        <div className="relative col-span-6">
          <Input
            label="Method Name"
            inputPlaceholder={"Enter Method Name"}
            inputName="name"
            value={values?.name || ""}
            onChange={handleChange}
            {...formValidate(errors, "name")}
          />
        </div>
        <div className="relative col-span-6">
          <FileUploadSingle
            size="small"
            label={"Upload Logo Image"}
            value={values?.image}
            onChange={(file) =>
              handleChange({ target: { name: "image", value: file } })
            }
            {...formValidate(errors, "image")}
          />
        </div>

        <div className="relative col-span-12">
          <div className="flex items-center gap-3 mt-2 mb-4">
            {options?.map((data, index) => (
              <Radio
                key={index}
                radioLabel={`${data?.item}`}
                radioName={"redio_item"}
                radioLableClass="font-CanelaDeck !text-gable-green font-medium !text-base"
                value={data?.value}
                onChange={handleRadioChange}
                isChecked={selectedOption === data?.value}
              />
            ))}
          </div>
          {selectedOption === "app" ? (
            <>
              <div className="grid grid-cols-12 gap-x-4 gap-y-4">
                <div className="relative col-span-12">
                  <div className="text-lg font-bold">Step One</div>
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="Display Name"
                    inputPlaceholder={"Enter Display Name"}
                    inputName="headingText"
                    value={values?.headingText || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "headingText")}
                  />
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="User Name"
                    inputPlaceholder={"Enter User Name"}
                    inputName="appUserName"
                    value={values?.appUserName || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "appUserName")}
                  />
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="Email"
                    inputPlaceholder={"Enter Email"}
                    inputName="appEmail"
                    value={values?.appEmail || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "appEmail")}
                  />
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="Code"
                    inputPlaceholder={"Enter Code"}
                    inputName="appId"
                    value={values?.appId || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "appId")}
                  />
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="Display Link"
                    inputPlaceholder={"Enter Link"}
                    inputName="displayLink"
                    value={values?.displayLink || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "displayLink")}
                  />
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="Enter Note/Purpose"
                    inputPlaceholder={"Enter Note/Purpose"}
                    inputName="noteAppPurpose"
                    value={values?.noteAppPurpose || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "noteAppPurpose")}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-4">
                    <Checkbox
                      checkboxLabel="Pay via scanner"
                      checkboxLableClass="font-bold !text-sm text-secondayColor"
                      checked={uploadSection === "scan"}
                      onChange={() =>
                        setUploadSection(
                          uploadSection === "scan" ? null : "scan"
                        )
                      }
                    />

                    <Checkbox
                      checkboxLabel="Pay via link"
                      checkboxLableClass="font-bold !text-sm text-secondayColor"
                      checked={uploadSection === "link"}
                      onChange={() =>
                        setUploadSection(
                          uploadSection === "link" ? null : "link"
                        )
                      }
                    />
                  </div>
                </div>
                <div className="col-span-12">
                  {uploadSection === "scan" && (
                    <div className="relative w-full">
                      <FileUploadSingle
                        label="Upload Scanner Image"
                        labelClasses="!mb-2"
                        value={values?.paymentImage}
                        onChange={(file) =>
                          handleChange({
                            target: { name: "paymentImage", value: file },
                          })
                        }
                        {...formValidate(errors, "paymentImage")}
                      />
                    </div>
                  )}

                  {uploadSection === "link" && (
                    <div className="relative w-full md:col-span-6">
                      <Input
                        label="Link"
                        inputPlaceholder="Enter Link"
                        inputName="paymentUrl"
                        value={values?.paymentUrl || ""}
                        onChange={handleChange}
                        {...formValidate(errors, "paymentUrl")}
                      />
                    </div>
                  )}
                </div>

                <div className="relative col-span-12">
                  <div className="text-lg font-bold">Step Two</div>
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="City"
                    inputPlaceholder={"Enter City"}
                    inputName="city"
                    value={values?.city || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "city")}
                  />
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="State"
                    inputPlaceholder={"Enter State"}
                    inputName="state"
                    value={values?.state || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "state")}
                  />
                </div>
                <div className="relative col-span-6">
                  <Input
                    label="Country"
                    inputPlaceholder={"Enter Country"}
                    inputName="country"
                    value={values?.country || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "country")}
                  />
                </div>
                <div className="relative col-span-12">
                  <Textarea
                    label={"Note"}
                    labelClasses={"mb-2 !text-gable-green"}
                    inputClasses={"bg-transparent rounded-lg"}
                    inputPlaceholder={"Write your note"}
                    inputName="appNote"
                    inputValue={values?.appNote || ""}
                    onChange={handleChange}
                    {...formValidate(errors, "appNote")}
                  />
                </div>
                <div className="relative col-span-12 space-y-4">
                  <div className="space-y-4">
                    {values?.appPointField?.map((item, i) => (
                      <div
                        key={i?.toString()}
                        className="flex items-end space-x-4"
                      >
                        <div className="flex-shrink flex-grow w-1/2">
                          <Input
                            label={`${numberToOrdinal(i)} Point`}
                            value={item?.point || ""}
                            inputPlaceholder={"Enter Point"}
                            onChange={(e) => {
                              setFieldsValue({
                                appPointField: values?.appPointField?.map(
                                  (item, index) =>
                                    i === index
                                      ? { ...item, point: e.target.value }
                                      : item
                                ),
                              });
                            }}
                          />
                        </div>
                        <div className="flex-shrink-0 !ml-0">
                          <Button
                            buttonIcon={"fa-regular fa-trash-can"}
                            buttonClasses={
                              "!bg-transparent !text-chestnut-rose"
                            }
                            buttonIconPosition={"left"}
                            buttonFunction={() => {
                              setFieldsValue({
                                appPointField: values?.appPointField?.filter(
                                  (item, index) => i !== index
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center">
                    <Button
                      buttonClasses={
                        "!text-chestnut-rose !h-6 !p-0 !rounded-none !bg-transparent"
                      }
                      buttonLabel={"Add Point"}
                      buttonLabelClasses={"!text-sm"}
                      buttonFunction={() => appPointAdd()}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-12 gap-x-4 gap-y-4">
              <div className="relative col-span-12">
                <div className="text-lg font-bold">Bank Transfer Details</div>
              </div>
              <div className="relative col-span-6">
                <Input
                  label="Account Name"
                  inputPlaceholder={"Enter Account Name"}
                  inputName="accName"
                  value={values?.accName || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "accName")}
                />
              </div>
              <div className="relative col-span-6">
                <Input
                  label="Bank Name"
                  inputPlaceholder={"Enter Bank Name"}
                  inputName="bankName"
                  value={values?.bankName || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "bankName")}
                />
              </div>
              <div className="relative col-span-6">
                <Input
                  label="Account Number"
                  inputPlaceholder={"Enter Account Number"}
                  inputName="accNumber"
                  value={values?.accNumber || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "accNumber")}
                />
              </div>
              <div className="relative col-span-6">
                <Input
                  label="Routing Number (for U.S. transfers)"
                  inputPlaceholder={"Enter Routing Number"}
                  inputName="routingNumber"
                  value={values?.routingNumber || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "routingNumber")}
                />
              </div>
              <div className="relative col-span-6">
                <Input
                  label="SWIFT Code (for international transfers)"
                  inputPlaceholder={"Enter SWIFT Code"}
                  inputName="swiftCode"
                  value={values?.swiftCode || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "swiftCode")}
                />
              </div>
              <div className="relative col-span-6">
                <Input
                  label="Bank Address"
                  inputPlaceholder={"Enter Bank Address"}
                  inputName="bankAddress"
                  value={values?.bankAddress || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "bankAddress")}
                />
              </div>
              <div className="relative col-span-6">
                <Input
                  label="Enter Note/Purpose"
                  inputPlaceholder={"Enter Note/Purpose"}
                  inputName="noteBankPurpose"
                  value={values?.noteBankPurpose || ""}
                  onChange={handleChange}
                  {...formValidate(errors, "noteBankPurpose")}
                />
              </div>
              <div className="relative col-span-12">
                <div className="text-lg font-bold">
                  After Sending Your Transfer
                </div>
              </div>
              <div className="relative col-span-12">
                {/* <Textarea
                  label={"Note"}
                  labelClasses={"mb-2 !text-gable-green"}
                  inputClasses={"bg-transparent rounded-lg"}
                  inputPlaceholder={"Write your note"}
                  inputName="bankNote"
                  inputValue={values?.bankNote}
                  onChange={handleChange}
                  {...formValidate(errors, "bankNote")}
                /> */}
                <Editor
                  label="Note"
                  placeholder="Write your note"
                  value={values?.bankNote}
                  onChange={(val) =>
                    handleChange({ target: { name: "bankNote", value: val } })
                  }
                  {...formValidate(errors, "bankNote")}
                />
              </div>
              <div className="relative col-span-12 space-y-4">
                <div className="space-y-4">
                  {values?.bankPointField?.map((item, i) => (
                    <div
                      key={i?.toString()}
                      className="flex items-end space-x-4"
                    >
                      <div className="flex-shrink flex-grow w-1/2">
                        <Input
                          label={`${numberToOrdinal(i)} Point`}
                          value={item?.point || ""}
                          inputPlaceholder={"Enter Point"}
                          onChange={(e) => {
                            setFieldsValue({
                              bankPointField: values?.bankPointField?.map(
                                (item, index) =>
                                  i === index
                                    ? { ...item, point: e.target.value }
                                    : item
                              ),
                            });
                          }}
                        />
                      </div>
                      <div className="flex-shrink-0 !ml-0">
                        <Button
                          buttonIcon={"fa-regular fa-trash-can"}
                          buttonClasses={"!bg-transparent !text-chestnut-rose"}
                          buttonIconPosition={"left"}
                          buttonFunction={() => {
                            setFieldsValue({
                              bankPointField: values?.bankPointField?.filter(
                                (item, index) => i !== index
                              ),
                            });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center">
                  <Button
                    buttonClasses={
                      "!text-chestnut-rose !h-6 !p-0 !rounded-none !bg-transparent"
                    }
                    buttonLabel={"Add Point"}
                    buttonLabelClasses={"!text-sm"}
                    buttonFunction={() => bankPointAdd()}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="relative col-span-12">
          <Editor
            label="Note"
            placeholder="Write your note"
            value={values?.note}
            onChange={(val) =>
              handleChange({ target: { name: "note", value: val } })
            }
            {...formValidate(errors, "note")}
          />
        </div>
      </div>
      <div className="relative col-span-4 mt-5">
        <div className="flex items-center justify-center gap-4">
          <Button
            buttonType="submit"
            buttonLabel={"Save"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
          <Button
            buttonLabel={"Cancel"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            buttonFunction={close}
          />
        </div>
      </div>
    </form>
  );
};

export default ManagePayment;
